import { useToast } from '@chakra-ui/react';
import {
  ExternalIngestionDocumentsForFolderDocument,
  useCreateOrUpdateIngestionDocumentReviewMutation,
  useCreateOrUpdateIngestionDocumentValidationErrorReviewMutation,
  useCreateOrUpdateIngestionFolderValidationErrorReviewMutation,
  useDeleteIngestionDocumentValidationErrorReviewMutation,
  useDeleteIngestionFolderValidationErrorReviewMutation,
  useExternalGetIngestionDocumentQuery,
  useExternalGetIngestionFolderQuery,
  useExternalGetValidationReportDataQuery,
  useExternalIngestionDocumentsForFolderQuery,
  useExternalSubmitValidationReviewsMutation,
  useGetIngestionDocumentReviewsQuery,
  useGetIngestionDocumentValidationErrorReviewsQuery,
  useGetIngestionFolderValidationErrorReviewsQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  ExternalSubmitValidationReviewsInput,
  IngestionDocumentReviewStatus,
} from '@client/graphql/__generated__/types';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganizationCompliancePage } from '~/apps/consumer/pages/Organizations/Compliance/OrganizationCompliancePageContext';
import { ComplianceDrawer } from '~/common/pages/Compliance/ComplianceDrawer';
import { IssueFormData } from '~/common/pages/Compliance/components/AddIssueModal';
import { useComplianceFolderSearchParam } from '~/common/pages/Compliance/hooks/useComplianceFolderSearchParam';
import { groupIngestionDocuments } from '~/common/pages/Compliance/utils/ingestionDocuments.utils';

export const ExternalComplianceDrawer: FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [selectedIngestionDocumentId, setSelectedIngestionDocumentId] =
    useComplianceFolderSearchParam('document', { historyMode: 'push' });

  const { organizationId } = useOrganizationCompliancePage();

  const { folderId } = useParams<{ folderId: string }>();

  const {
    data: folderData,
    loading: folderLoading,
    error: folderError,
  } = useExternalGetIngestionFolderQuery({
    variables: {
      input: {
        organizationId: organizationId!,
        ingestionFolderId: folderId!,
      },
      includeTransactionPipeline: true,
      includeKeyTerms: true,
    },
    skip: !organizationId || !folderId,
    // avoid global toast; show inline state instead
    context: { skipGlobalErrorHandler: true },
  });

  const { data: documentsData, loading: documentsLoading } =
    useExternalIngestionDocumentsForFolderQuery({
      variables: {
        input: {
          ingestionFolderId: folderId!,
        },
      },
      skip: !folderId,
    });

  const {
    data: selectedIngestionDocumentData,
    loading: selectedIngestionDocumentLoading,
    error: selectedIngestionDocumentError,
  } = useExternalGetIngestionDocumentQuery({
    variables: {
      input: {
        ingestionDocumentId: selectedIngestionDocumentId as string,
      },
    },
    fetchPolicy: 'cache-first',
    // avoid global toast; show inline state instead
    context: { skipGlobalErrorHandler: true },
    skip: !selectedIngestionDocumentId,
  });
  const selectedIngestionDocument =
    selectedIngestionDocumentData?.externalGetIngestionDocument || null;

  const { data: validationData, loading: validationLoading } =
    useExternalGetValidationReportDataQuery({
      variables: {
        input: {
          ingestionFolderId: folderId!,
        },
      },
      skip: !folderId,
    });

  const currentFolder = folderData?.externalGetIngestionFolder || null;

  const { data: folderValidationErrorReviewsData } =
    useGetIngestionFolderValidationErrorReviewsQuery({
      variables: {
        input: {
          ingestionFolderValidationId:
            validationData?.externalGetValidationReportData?.folderValidation
              ?.id,
        },
      },
      skip: !validationData?.externalGetValidationReportData?.folderValidation
        ?.id,
    });

  const { data: documentValidationErrorReviewsData } =
    useGetIngestionDocumentValidationErrorReviewsQuery({
      variables: {
        input: {
          ingestionFolderId: folderId || '',
        },
      },
      skip: !folderId,
    });

  const { data: documentReviewsData } = useGetIngestionDocumentReviewsQuery({
    variables: {
      input: {
        ingestionFolderId: folderId || undefined,
      },
    },
    skip: !folderId,
  });

  const [createOrUpdateFolderReview] =
    useCreateOrUpdateIngestionFolderValidationErrorReviewMutation();
  const [deleteFolderReview] =
    useDeleteIngestionFolderValidationErrorReviewMutation();
  const [createOrUpdateDocumentValidationReview] =
    useCreateOrUpdateIngestionDocumentValidationErrorReviewMutation();
  const [deleteDocumentValidationReview] =
    useDeleteIngestionDocumentValidationErrorReviewMutation();
  const [createOrUpdateDocumentReview] =
    useCreateOrUpdateIngestionDocumentReviewMutation();
  const [submitValidationReviews] =
    useExternalSubmitValidationReviewsMutation();

  // Filter user added issues from folder validation error reviews
  const userAddedFolderIssues =
    folderValidationErrorReviewsData?.externalGetIngestionFolderValidationErrorReviews?.filter(
      (review) => review.isAddedByUser
    ) || [];

  // Filter hidden validation issues (not user-added)
  const hiddenFolderIssues =
    folderValidationErrorReviewsData?.externalGetIngestionFolderValidationErrorReviews?.filter(
      (review) => review.isHidden && !review.isAddedByUser
    ) || [];

  // Get document validation error reviews
  const allDocumentValidationReviews =
    documentValidationErrorReviewsData?.externalGetIngestionDocumentValidationErrorReviews ||
    [];

  // Separate user-added and hidden document validation reviews
  const userAddedDocumentIssues = allDocumentValidationReviews.filter(
    (review) => review.isAddedByUser
  );

  // Only include hidden reviews that are not user-added (for hiding system validations)
  const hiddenDocumentValidationReviews = allDocumentValidationReviews.filter(
    (review) => review.isHidden && !review.isAddedByUser
  );

  // Separate submitted and unsubmitted document reviews from the same query
  const { documentReviews, submittedDocumentReviews } = useMemo(() => {
    // Get all document reviews
    const allDocumentReviews =
      documentReviewsData?.externalGetIngestionDocumentReviews || [];

    // Filter to only reviews for documents in this folder
    const filteredReviews = allDocumentReviews.filter((review) => {
      const matchingDocument =
        documentsData?.externalIngestionDocumentsForFolder?.find(
          (doc) => doc.id === review.ingestionDocument.id
        );

      if (!matchingDocument) return false;

      // If the ingestion document has a version, check that it matches the review version
      if (matchingDocument.version && review.version) {
        return matchingDocument.version === review.version;
      }

      // If no version on document or review, include it
      return true;
    });

    // Separate submitted vs unsubmitted reviews
    // Reviews tied to a submission are considered "submitted", others are "unsubmitted"
    const submitted: typeof filteredReviews = [];
    const unsubmitted: typeof filteredReviews = [];

    filteredReviews.forEach((review) => {
      if (review.submission) {
        submitted.push(review);
      } else {
        unsubmitted.push(review);
      }
    });

    // For submitted reviews, keep only the latest one per ingestion document
    const latestSubmittedByDocument = new Map<string, (typeof submitted)[0]>();

    submitted.forEach((review) => {
      const docId = review.ingestionDocument.id;
      const existing = latestSubmittedByDocument.get(docId);

      if (
        !existing ||
        new Date(review.reviewedAt as string) >
          new Date(existing.reviewedAt as string)
      ) {
        latestSubmittedByDocument.set(docId, review);
      }
    });

    return {
      documentReviews: unsubmitted,
      submittedDocumentReviews: Array.from(latestSubmittedByDocument.values()),
    };
  }, [
    documentReviewsData?.externalGetIngestionDocumentReviews,
    documentsData?.externalIngestionDocumentsForFolder,
  ]);

  // Find the current document's review if it exists
  const currentDocumentReview = useMemo(() => {
    if (!selectedIngestionDocumentId) return null;

    return (
      documentReviews.find(
        (review) => review.ingestionDocument.id === selectedIngestionDocumentId
      ) || null
    );
  }, [documentReviews, selectedIngestionDocumentId]);

  // Get documents that need review using the same grouping logic as the sidebar
  const documentsNeedingReview = useMemo(() => {
    const documents = documentsData?.externalIngestionDocumentsForFolder || [];
    const grouped = groupIngestionDocuments(
      documents,
      validationData?.externalGetValidationReportData || null,
      submittedDocumentReviews
    );

    return grouped.needsReviewDocuments;
  }, [
    documentsData?.externalIngestionDocumentsForFolder,
    validationData?.externalGetValidationReportData,
    submittedDocumentReviews,
  ]);

  // Helper function to get the next document to select after reviewing current document
  const getNextDocumentToSelect = (currentDocumentId: string) => {
    const currentIndex = documentsNeedingReview.findIndex(
      (doc) => doc.id === currentDocumentId
    );

    if (currentIndex >= 0 && currentIndex < documentsNeedingReview.length - 1) {
      // Return next document in the needsReview list
      return documentsNeedingReview[currentIndex + 1];
    }

    // If current document is last or not found, keep the same document
    return null;
  };

  // Callback functions for folder issue management
  const handleAddFolderIssue = async (issueData: IssueFormData) => {
    const folderValidationId =
      validationData?.externalGetValidationReportData?.folderValidation?.id;
    if (!folderValidationId) return;

    await createOrUpdateFolderReview({
      variables: {
        input: {
          ingestionFolderValidationId: folderValidationId,
          message: issueData.message,
          isAddedByUser: true,
          isHidden: false,
        },
      },
      refetchQueries: ['getIngestionFolderValidationErrorReviews'],
    });
  };

  const handleDeleteFolderIssue = async (reviewId: string) => {
    await deleteFolderReview({
      variables: {
        input: {
          reviewId: reviewId,
        },
      },
      refetchQueries: ['getIngestionFolderValidationErrorReviews'],
    });
  };

  const handleHideFolderIssue = async (
    isHidden: boolean,
    message?: string,
    documentCategory?: string,
    documentForm?: string,
    documentName?: string,
    ruleUuid?: string
  ) => {
    const folderValidationId =
      validationData?.externalGetValidationReportData?.folderValidation?.id;
    const hasDocumentContext = Boolean(
      documentCategory || documentForm || documentName
    );
    const hasRuleContext = Boolean(ruleUuid);

    if (!folderValidationId || (!hasDocumentContext && !hasRuleContext)) return;

    await createOrUpdateFolderReview({
      variables: {
        input: {
          ingestionFolderValidationId: folderValidationId,
          documentCategory,
          documentForm,
          documentName,
          ruleUuid,
          isHidden,
          message,
          isAddedByUser: false,
        },
      },
      refetchQueries: ['getIngestionFolderValidationErrorReviews'],
    });
  };

  const handleHideDocumentValidation = async (
    isHidden: boolean,
    message?: string,
    ingestionDocumentValidationId?: string,
    ruleUuid?: string,
    contractDocumentUuid?: string
  ) => {
    if (
      !selectedIngestionDocumentId ||
      !ingestionDocumentValidationId ||
      !ruleUuid ||
      !contractDocumentUuid
    ) {
      return;
    }

    await createOrUpdateDocumentValidationReview({
      variables: {
        input: {
          ingestionDocumentId: selectedIngestionDocumentId,
          ingestionDocumentValidationId,
          contractDocumentUuid,
          ruleUuid,
          isHidden,
          isAddedByUser: false,
          message: message?.trim() || undefined,
        },
      },
      refetchQueries: ['getIngestionDocumentValidationErrorReviews'],
    });
  };

  const handleAddDocumentIssue = async (
    issueData: IssueFormData,
    contractDocumentUuid?: string
  ) => {
    if (!selectedIngestionDocumentId) {
      return;
    }

    await createOrUpdateDocumentValidationReview({
      variables: {
        input: {
          ingestionDocumentId: selectedIngestionDocumentId,
          contractDocumentUuid, // Now includes the specific contract document
          ruleUuid: undefined, // No rule for user-added issues
          isHidden: false,
          isAddedByUser: true,
          message: issueData.message,
        },
      },
      refetchQueries: ['getIngestionDocumentValidationErrorReviews'],
    });
  };

  const handleDeleteDocumentIssue = async (reviewId: string) => {
    await deleteDocumentValidationReview({
      variables: {
        input: {
          reviewId,
        },
      },
      refetchQueries: ['getIngestionDocumentValidationErrorReviews'],
    });
  };

  const handleCreateOrUpdateDocumentReview = async (
    ingestionDocumentId: string,
    status: IngestionDocumentReviewStatus,
    version?: string,
    reviewId?: string
  ) => {
    await createOrUpdateDocumentReview({
      variables: {
        input: {
          reviewId,
          ingestionDocumentId,
          status,
          version,
        },
      },
      refetchQueries: ['getIngestionDocumentReviews'],
    });

    // After approving or rejecting a document, automatically select the next document that needs review
    if (
      status === IngestionDocumentReviewStatus.APPROVED ||
      status === IngestionDocumentReviewStatus.RETURNED
    ) {
      const nextDocument = getNextDocumentToSelect(ingestionDocumentId);
      if (nextDocument) {
        setSelectedIngestionDocumentId(nextDocument.id);
      }
      // If nextDocument is null, keep the current document selected (it's the last one)
    }
  };

  const handleSubmitValidationReviews = async (
    input: Omit<ExternalSubmitValidationReviewsInput, 'ingestionFolderId'>
  ) => {
    if (!folderId) return;

    try {
      await submitValidationReviews({
        variables: {
          input: {
            ingestionFolderId: folderId,
            ...input,
          },
        },
        refetchQueries: [
          'getIngestionDocumentReviews',
          'getIngestionDocumentValidationErrorReviews',
          'getIngestionFolderValidationErrorReviews',
          // The documents weren't getting properly refetched
          {
            query: ExternalIngestionDocumentsForFolderDocument,
            variables: {
              input: {
                ingestionFolderId: folderId,
              },
            },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: 'Failed to submit validation reviews',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isDrawerOpen = Boolean(folderId);
  const onClose = () => {
    navigate('/organizations/compliance');
  };

  return (
    <ComplianceDrawer
      currentDocumentReview={currentDocumentReview}
      documentReviews={documentReviews}
      documents={documentsData?.externalIngestionDocumentsForFolder || []}
      folder={currentFolder}
      folderLoadError={!!folderId && !!folderError && !currentFolder}
      hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
      hiddenFolderIssues={hiddenFolderIssues}
      isOpen={isDrawerOpen}
      loading={folderLoading || documentsLoading || validationLoading}
      selectedDocumentLoadError={
        !!selectedIngestionDocumentId && !!selectedIngestionDocumentError
      }
      selectedDocumentLoading={selectedIngestionDocumentLoading}
      selectedIngestionDocument={selectedIngestionDocument}
      selectedIngestionDocumentId={selectedIngestionDocumentId}
      setSelectedIngestionDocumentId={setSelectedIngestionDocumentId}
      submittedDocumentReviews={submittedDocumentReviews}
      userAddedDocumentIssues={userAddedDocumentIssues}
      userAddedFolderIssues={userAddedFolderIssues}
      validationData={validationData?.externalGetValidationReportData || null}
      variant="external"
      onAddDocumentIssue={handleAddDocumentIssue}
      onAddFolderIssue={handleAddFolderIssue}
      onClose={onClose}
      onCreateOrUpdateDocumentReview={handleCreateOrUpdateDocumentReview}
      onDeleteDocumentIssue={handleDeleteDocumentIssue}
      onDeleteFolderIssue={handleDeleteFolderIssue}
      onHideDocumentValidation={handleHideDocumentValidation}
      onHideFolderIssue={handleHideFolderIssue}
      onSubmitValidationReviews={handleSubmitValidationReviews}
    />
  );
};
